import { forwardRef, useState } from "react";
import type { FC } from "react";
import { AssistantModalPrimitive } from "@assistant-ui/react";
import { Box, IconButton, styled, Tooltip, Typography } from "@mui/material";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";

import { Thread } from "./thread";

// Styled Components
const ModalAnchor = styled(Box)(() => ({
  position: "fixed",
  right: "24px",
  bottom: "24px",
  width: "56px",
  height: "56px",
  zIndex: 1300,
}));

const ModalTriggerButton = styled(IconButton)(({ theme }) => ({
  width: "56px",
  height: "56px",
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
  color: "#fff",
  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
    transform: "scale(1.1)",
    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.25)",
  },
  "&:active": {
    transform: "scale(0.95)",
  },
}));

const ModalContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{ expanded?: boolean }>(({ theme, expanded }) => ({
  width: expanded ? "700px" : "400px",
  height: expanded ? "80vh" : "500px",
  maxHeight: "800px",
  backgroundColor: theme.palette.background.paper,
  borderRadius: "16px",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  zIndex: 1300,
  transition: "width 0.3s ease, height 0.3s ease",
}));

const ModalHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 16px",
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  flexShrink: 0,
}));

const HeaderTitle = styled(Typography)({
  fontWeight: 600,
  fontSize: "14px",
});

const BotIconStyled = styled(SmartToyOutlinedIcon)({
  position: "absolute",
  transition: "transform 0.3s ease, opacity 0.3s ease",
  "&[data-state='open']": {
    transform: "rotate(90deg) scale(0)",
    opacity: 0,
  },
  "&[data-state='closed']": {
    transform: "rotate(0deg) scale(1)",
    opacity: 1,
  },
});

const ChevronIconStyled = styled(KeyboardArrowDownIcon)({
  position: "absolute",
  transition: "transform 0.3s ease, opacity 0.3s ease",
  "&[data-state='open']": {
    transform: "rotate(0deg) scale(1)",
    opacity: 1,
  },
  "&[data-state='closed']": {
    transform: "rotate(-90deg) scale(0)",
    opacity: 0,
  },
});

type AssistantModalButtonProps = { "data-state"?: "open" | "closed" };

const AssistantModalButton = forwardRef<HTMLButtonElement, AssistantModalButtonProps>(
  ({ "data-state": state, ...rest }, ref) => {
    const tooltip = state === "open" ? "Close Assistant" : "Open Assistant";

    return (
      <Tooltip title={tooltip} placement="left">
        <ModalTriggerButton ref={ref} {...rest}>
          <BotIconStyled data-state={state} />
          <ChevronIconStyled data-state={state} />
        </ModalTriggerButton>
      </Tooltip>
    );
  },
);

AssistantModalButton.displayName = "AssistantModalButton";

export const AssistantModal: FC = () => {
  const [expanded, setExpanded] = useState(false);

  const toggleExpand = () => setExpanded((prev) => !prev);

  return (
    <AssistantModalPrimitive.Root>
      <AssistantModalPrimitive.Anchor asChild>
        <ModalAnchor>
          <AssistantModalPrimitive.Trigger asChild>
            <AssistantModalButton />
          </AssistantModalPrimitive.Trigger>
        </ModalAnchor>
      </AssistantModalPrimitive.Anchor>
      <AssistantModalPrimitive.Content sideOffset={16} asChild>
        <ModalContent expanded={expanded}>
          <ModalHeader>
            <HeaderTitle>OpsHelm Assistant</HeaderTitle>
            <Tooltip title={expanded ? "Collapse" : "Expand"}>
              <IconButton size="small" onClick={toggleExpand}>
                {expanded ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </ModalHeader>
          <Thread />
        </ModalContent>
      </AssistantModalPrimitive.Content>
    </AssistantModalPrimitive.Root>
  );
};

export default AssistantModal;
